import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Library, Plus, Pencil, Trash2, Eye, EyeOff, Tag, X, ArrowLeft, ExternalLink,
  Sparkles, Loader2, BookOpen, Download, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import { useAuth } from '@/context/AuthContext';

/**
 * Admin-only resource manager. Lists all resources (published &
 * unpublished), allows create/edit/delete and category management.
 * Mirrors BlogEditorPage's layout for UI consistency.
 */
export default function ResourceManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | resource object
  const [newCategory, setNewCategory] = useState('');

  const refreshAll = useCallback(async () => {
    setLoading(true);
    const [resRes, catRes, postsRes] = await Promise.all([
      apiFetch('/api/resources/admin/list'),
      apiFetch('/api/resources/categories'),
      apiFetch('/api/resources/admin/blog-posts'),
    ]);
    if (!resRes.error && resRes.data) setResources(resRes.data.resources || []);
    if (!catRes.error && catRes.data) setCategories(catRes.data.categories || []);
    if (!postsRes.error && postsRes.data) setBlogPosts(postsRes.data.posts || []);
    setLoading(false);
  }, []);

  // useRef declared early (before any conditional return) to keep
  // rules-of-hooks happy. Used by the Restore <input type="file">.
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (user?.is_admin) refreshAll();
  }, [user, refreshAll]);

  if (user && !user.is_admin) {
    return (
      <div className="container mx-auto max-w-md px-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">Admins only.</p>
        <Button variant="link" onClick={() => navigate('/resources')}>Back to Resources</Button>
      </div>
    );
  }

  const handleSave = async (payload) => {
    const isNew = editing === 'new';
    const endpoint = isNew
      ? '/api/resources/admin/create'
      : `/api/resources/admin/${editing.resource_id}`;
    const method = isNew ? 'POST' : 'PATCH';
    const { error } = await apiFetch(endpoint, { method, body: payload });
    if (error) {
      toast.error(error || 'Save failed');
      return;
    }
    toast.success(isNew ? 'Resource created' : 'Resource updated');
    setEditing(null);
    await refreshAll();
  };

  const handleDelete = async (resource) => {
    if (!window.confirm(`Delete "${resource.name}"?`)) return;
    const { error } = await apiFetch(`/api/resources/admin/${resource.resource_id}`, { method: 'DELETE' });
    if (error) {
      toast.error(error || 'Delete failed');
      return;
    }
    toast.success('Resource deleted');
    await refreshAll();
  };

  const handleAddCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    const { data, error } = await apiFetch('/api/resources/admin/categories', {
      method: 'POST', body: { name },
    });
    if (error) { toast.error(error || 'Failed to add'); return; }
    if (data?.added === false) toast.info(data.message || 'Already exists');
    else toast.success(`Added "${name}"`);
    setNewCategory('');
    await refreshAll();
  };

  const handleDeleteCategory = async (name) => {
    if (!window.confirm(`Delete category "${name}"? It will be removed from any resources using it.`)) return;
    const { error } = await apiFetch(`/api/resources/admin/categories/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (error) { toast.error(error || 'Delete failed'); return; }
    toast.success(`Deleted "${name}"`);
    await refreshAll();
  };

  // Download every resource + category as a JSON file. Pure safety net
  // — resources live in MongoDB and survive redeploys regardless, but
  // having a portable copy on disk is reassuring after the recent
  // file-loss scares (emoji + QR logo on-disk uploads).
  const handleBackup = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resources/admin/backup`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      // Pull the filename out of Content-Disposition (e.g.
      // `attachment; filename="resources-backup-...json"`).
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `resources-backup-${Date.now()}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded.');
    } catch (e) {
      toast.error(e.message || 'Backup failed');
    }
  };

  const handleRestoreFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data?.resources)) {
        throw new Error('Invalid backup file — missing `resources` array');
      }
      const count = data.resources.length;
      const mode = window.confirm(
        `Restore ${count} resource${count === 1 ? '' : 's'} from "${file.name}"?\n\n` +
        `OK = MERGE (existing IDs updated, nothing deleted, new ones inserted)\n` +
        `Cancel = abort.`
      ) ? 'merge' : null;
      if (!mode) return;
      const { data: out, error } = await apiFetch('/api/resources/admin/restore', {
        method: 'POST',
        body: { resources: data.resources, categories: data.categories, mode },
      });
      if (error) throw new Error(error);
      toast.success(
        `Restore complete — ${out.resources_inserted} new, ${out.resources_updated} updated.`
      );
      await refreshAll();
    } catch (e) {
      toast.error(e.message || 'Restore failed');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 sm:py-8" data-testid="resource-manager-page">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/resources')} data-testid="rm-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Library className="h-5 w-5 text-[#00A896]" /> Manage Resources
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleBackup}
            variant="outline"
            size="sm"
            data-testid="rm-backup-btn"
            title="Download every resource + category as a JSON file"
          >
            <Download className="h-4 w-4 mr-1" /> Backup
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => handleRestoreFile(e.target.files?.[0])}
            data-testid="rm-restore-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            size="sm"
            data-testid="rm-restore-btn"
            title="Re-import a backup JSON (merge — nothing is deleted)"
          >
            <Upload className="h-4 w-4 mr-1" /> Restore
          </Button>
          <Button onClick={() => setEditing('new')} data-testid="rm-add-resource-btn">
            <Plus className="h-4 w-4 mr-1" /> New resource
          </Button>
        </div>
      </div>

      {/* Categories panel */}
      <Card className="mb-4" data-testid="rm-categories-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4" /> Categories</CardTitle>
          <CardDescription className="text-xs">Add or delete categories that resources can be tagged with.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            {categories.map((c) => (
              <Badge key={c} variant="secondary" className="text-xs pr-1 flex items-center gap-1" data-testid={`rm-cat-${c}`}>
                {c}
                <button
                  type="button"
                  onClick={() => handleDeleteCategory(c)}
                  className="hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Delete ${c}`}
                  data-testid={`rm-cat-del-${c}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories yet.</span>}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="New category name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              className="flex-1"
              data-testid="rm-new-category-input"
            />
            <Button onClick={handleAddCategory} size="sm" data-testid="rm-add-category-btn">Add</Button>
          </div>
        </CardContent>
      </Card>

      {/* Resources list */}
      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : resources.length === 0 ? (
        <Card><CardContent className="py-6 text-center text-muted-foreground">No resources yet. Add your first one!</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {resources.map((r) => (
            <Card key={r.resource_id} data-testid={`rm-row-${r.resource_id}`}>
              <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm sm:text-base line-clamp-2 sm:truncate min-w-0">{r.name}</h3>
                    <Badge variant={r.published ? 'default' : 'secondary'} className="text-[10px]">
                      {r.published ? 'Published' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate" title={r.url}>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                      {r.url} <ExternalLink className="h-3 w-3" />
                    </a>
                  </p>
                  {r.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {r.categories.map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(r)} data-testid={`rm-edit-${r.resource_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(r)} data-testid={`rm-del-${r.resource_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      {editing !== null && (
        <ResourceEditorDialog
          resource={editing === 'new' ? null : editing}
          categories={categories}
          blogPosts={blogPosts}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}


function ResourceEditorDialog({ resource, categories, blogPosts, onClose, onSave }) {
  const [name, setName] = useState(resource?.name || '');
  const [url, setUrl] = useState(resource?.url || '');
  const [description, setDescription] = useState(resource?.description || '');
  const [selectedCats, setSelectedCats] = useState(resource?.categories || []);
  const [published, setPublished] = useState(resource?.published ?? true);
  const [blogPostId, setBlogPostId] = useState(resource?.blog_post_id || '');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const toggleCategory = (c) => {
    setSelectedCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const handleGenerateDescription = async () => {
    if (!url.trim()) {
      toast.error('Add the URL first so we can read the site');
      return;
    }
    setGenerating(true);
    const { data, error } = await apiFetch('/api/resources/admin/ai-describe', {
      method: 'POST',
      body: { url: url.trim(), name: name.trim() || undefined },
    });
    setGenerating(false);
    if (error || !data?.description) {
      toast.error(error || 'Generation failed');
      return;
    }
    setDescription(data.description);
    toast.success('Description generated — edit freely');
  };

  const handleSubmit = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSaving(true);
    await onSave({
      name: name.trim(),
      url: url.trim(),
      description: description.trim(),
      categories: selectedCats,
      published,
      blog_post_id: blogPostId || null,
    });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rm-editor-dialog">
        <DialogHeader>
          <DialogTitle>{resource ? 'Edit resource' : 'New resource'}</DialogTitle>
          <DialogDescription className="text-xs">
            Resource name links out to the URL in a new tab when published.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="rm-name" className="text-xs font-medium">Resource name</Label>
            <Input id="rm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ADHD Foundation" data-testid="rm-editor-name-input" />
          </div>
          <div>
            <Label htmlFor="rm-url" className="text-xs font-medium">URL</Label>
            <Input id="rm-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="rm-editor-url-input" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label htmlFor="rm-desc" className="text-xs font-medium">Description (optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateDescription}
                disabled={generating || !url.trim()}
                className="h-7 text-xs gap-1.5"
                data-testid="rm-editor-ai-describe-btn"
              >
                {generating ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading site…</>
                ) : (
                  <><Sparkles className="h-3.5 w-3.5 text-[#7C3AED]" /> AI generate</>
                )}
              </Button>
            </div>
            <Textarea id="rm-desc" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 2000))} rows={3} placeholder="Short summary of what this resource offers… (or click ✨ AI generate to pull from the site)" maxLength={2000} data-testid="rm-editor-desc-input" />
            <p className="text-[10px] text-muted-foreground mt-0.5">{description.length} / 2000 — fully editable after AI generation</p>
          </div>
          <div>
            <Label className="text-xs font-medium">Categories (click to toggle)</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="rm-editor-categories">
              {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories yet — add some on the manage page first.</span>}
              {categories.map((c) => {
                const active = selectedCats.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`px-2 py-1 rounded-full text-xs border transition-colors ${active ? 'bg-[#00C9B3] text-[#0d1f2d] border-[#00C9B3]' : 'bg-muted border-border hover:bg-muted/70'}`}
                    data-testid={`rm-editor-cat-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5 text-[#7C3AED]" />
              Link to a blog post (optional)
            </Label>
            <Select value={blogPostId || 'none'} onValueChange={(v) => setBlogPostId(v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1" data-testid="rm-editor-blog-select">
                <SelectValue placeholder="No linked blog post" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No linked blog post</SelectItem>
                {blogPosts.map((p) => (
                  <SelectItem key={p.post_id} value={p.post_id}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Renders as a small clickable link under the resource description.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-2">
            <Label htmlFor="rm-pub" className="text-sm flex items-center gap-2">
              {published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              {published ? 'Published' : 'Draft'}
            </Label>
            <Switch id="rm-pub" checked={published} onCheckedChange={setPublished} data-testid="rm-editor-published-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="rm-editor-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="rm-editor-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
